import React, { useEffect, useMemo, useRef } from "react";
import styled, { keyframes } from "styled-components";
import Spinner from "react-bootstrap/Spinner";
import { InfoIcon } from "assets/icons";
import {
  Form,
  InputwithLabel,
  Formik,
  TimePickerField,
  Checkbox,
} from "ui/forms";
import moment from "moment";
import { BasicButton } from "ui/Buttons";
import { useState } from "react";
import axios from "utils/APIv2";
import {
  ManageSettingsData,
  ManageSettingsDataAdd,
  ReminderRecommendationData,
} from "mutations/adddiscount";
import Tooltip from "ui/Tooltip";
import EmailTemplateService from "services/emailTemplate.services";
import { useGetAllEmailTemplates } from "queries/emailTemplates";

// The rule engine writes its reasoning lowercase ("avg 43 days late over ...").
// Formatting here rather than at the source means recommendations already
// stored read correctly too, without waiting for the next lambda run.
//
// The 0% case is a rounding artefact: the line is only emitted when the share is
// above zero, so anything under half a percent prints as "0%" and reads like a
// contradiction. Saying it in words is closer to what it means.
const formatReasoning = (line) => {
  if (typeof line !== "string" || line.length === 0) return line;
  const text = line.replace(/^0% of /, "none of ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Both the manual and the recommended weekday dropdowns use this, inline, so
// the two shared `select` rules that reach this popup (the popover's square
// 25px one and the form's 40px pill) can't style the two sides differently.
const weekdaySelectStyle = {
  width: "150px",
  flex: "0 0 auto",
  height: "40px",
  lineHeight: "normal",
  padding: "0 8px",
  boxSizing: "border-box",
  border: "1px solid #707070",
  borderRadius: "4px",
  background: "#3a3b48",
  color: "#909091",
  fontFamily: "Montserrat Alternates",
  fontSize: "13px",
  margin: 0,
};

const TemplatePicker = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background: #3a3b48;
  border: 1px solid #707070;
  border-radius: 10px;
  .hint {
    font-family: Montserrat Alternates;
    font-size: 11px;
    line-height: 16px;
    color: #c4c4c4;
    margin-bottom: 8px;
  }
  select {
    width: 100%;
    height: 30px;
    padding: 0 8px;
    margin-bottom: 8px;
    background: #3a3b48;
    border: 1px solid #707070;
    border-radius: 10px;
    font-family: Montserrat Alternates;
    font-size: 12px;
    color: #c4c4c4;
  }
  .cancel {
    display: block;
    margin-top: 8px;
    text-align: center;
    font-family: Montserrat Alternates-semiBold;
    font-size: 11px;
    color: #808186;
    text-decoration: underline;
    cursor: pointer;
  }
`;
const ApplyError = styled.div`
  margin-top: 8px;
  font-family: Montserrat Alternates-semiBold;
  font-size: 11px;
  color: #e05353;
`;

const ReminderSection = styled.div`
  /* input:not([type="checkbox"]) {
    height: 13px;
    width: 28px;
    margin: 0px 5px;
    background: #3a3b48;
    border: 1px solid #707070;
    font-family: Montserrat Alternates;
    color: #909090;
    &:focus {
      border: 1px solid #707070;
      outline: none;
      color: #3a3b48;
      background-color: #ffffff;
    }
  } */

  input:not([type="checkbox"]) {
    height: 13px !important;
    padding: 8px !important;
    border-radius: 0px !important;
    margin: 0px 5px !important;
    font-size: 9px !important;
  }
`;
const StyleTimePicker = styled.div`
  .form-field {
    margin-bottom: 0px;
  }
  label {
    display: none;
  }
  input {
    width: 90px !important;
    background: #232631 !important ;
  }
`;

const HeadingText = styled.div`
  font-family: Montserrat Alternates-semiBold;
  font-size: 11px;
  line-height: 25px;
  color: #808186;
`;
const HeadingTextManageSettings = styled.div`
  font-family: Montserrat Alternates-Bold;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0px;
  color: #fff;
  padding-bottom: 10px;
`;
// Column headers under the main title: says which side you are reading, the
// settings you control or the ones being suggested to you.
const ColumnLabel = styled.div`
  /* Fixed height on both sides so the two labels line up across the divider —
     the badge on the right is taller than the text and would otherwise push it
     down out of line with the left. */
  display: flex;
  align-items: center;
  min-height: 22px;
  margin-bottom: 10px;
  font-family: Montserrat Alternates-semiBold;
  font-size: 11px;
  line-height: 15px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #808186;
`;
const Section = styled.div`
  border-bottom: 1px solid #707070;
  padding: 10px;
  padding-top: 2px;
`;
const CheckboxWrapper = styled.div`
  display: flex;
  align-items: baseline;
  input {
    background: transparent 0% 0% no-repeat padding-box !important;
    color: #808186 !important;
  }
  input[type="checkbox"] {
    margin-bottom: -3px;
    margin-right: 5px;
    background: transparent 0% 0% no-repeat padding-box;
    &:after {
      color: transparent;
    }
    &:checked {
      &:after {
        color: #33c2df;
      }
    }
  }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;
const checkPop = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;
const PopoverLayout = styled.div`
  display: flex;
  align-items: stretch;
`;
const LeftColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;
const VerticalDivider = styled.div`
  flex: 0 0 1px;
  background: #707070;
  margin: 10px 0;
`;
const RecommendationColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  /* 2px top matches the left column's Section, so both labels start level;
     no bottom padding so Apply sits at the same height as Save; no right
     padding because the popover body already pads that edge by 16px. */
  /* 10px left matches the Section padding the left column's rows sit in. */
  padding: 2px 0 0 10px;
  overflow: hidden;
  animation: ${fadeInUp} 0.3s ease;
`;
const RecommendationCard = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;
// The recommendation is shown as a read-only copy of the manual form on the
// left, so the two sides can be compared control-for-control. pointer-events
// rather than `disabled` on each input: disabled greys everything out, and the
// point here is that it looks the same as the side it mirrors.
const PreviewForm = styled.div`
  pointer-events: none;
  user-select: none;
`;
const NoRecommendationHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  font-size: 12px;
  color: #808186;
  text-align: center;
`;
const AppliedBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  color: #53c2a3;
  font-family: Montserrat Alternates-semiBold;
  font-size: 11px;
  text-align: center;
  .check {
    font-size: 22px;
    margin-bottom: 6px;
    animation: ${checkPop} 0.4s ease;
  }
`;
const RiskBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 11px;
  line-height: 14px; /* 14 + 6 padding = 20px, inside the 22px label row */
  font-family: Montserrat Alternates-semiBold;
  text-transform: uppercase;
  margin-left: 8px;
  color: #fff;
  background: ${(props) =>
    props.tier === "critical"
      ? "#e05353"
      : props.tier === "high"
      ? "#e0a353"
      : props.tier === "medium"
      ? "#cbb400"
      : "#53c2a3"};
`;
const ReasoningList = styled.ul`
  margin: 12px 0 0;
  padding-left: 18px;
  font-size: 11px;
  line-height: 19px;
  color: #808186;
`;
const LoadingHint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 140px;
  font-size: 10px;
  color: #808186;
  text-align: center;
  .spinner-border {
    width: 1.1rem;
    height: 1.1rem;
    border-width: 0.15em;
  }
`;
export default function CustomerReminder({
  customer,
  childFunc,
  handleReminderSettings,
  handleReminderDefaults,
  errorMessage,
  initialValues,
  closeModal,
  childEmailCheck,
  parentRecord,
}) {
  const formRef = useRef();
  // A template assigned from the recommendation panel counts straight away,
  // without waiting for the parent list to refetch the customer row.
  const [assignedTemplateId, setAssignedTemplateId] = useState("");
  const isEmailTemplateSelected =
    customer?.preferences?.emailTemplateId || assignedTemplateId;

  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [pickedTemplateId, setPickedTemplateId] = useState("");
  const [applyError, setApplyError] = useState("");
  const { data: templateData } = useGetAllEmailTemplates();
  const templates = Array.isArray(templateData) ? templateData : templateData?.data || [];
  const fetchData = ManageSettingsData();
  const dataAdd = ManageSettingsDataAdd();
  const recommendationFetch = ReminderRecommendationData();
  const [recommendation, setRecommendation] = useState(null);
  // Starts as "loading" so the panel shows a spinner from the first paint,
  // never a false "no suggestion" while the request hasn't gone out yet.
  const [recommendationStatus, setRecommendationStatus] = useState("loading"); // loading | ready | none
  // idle -> applied (shows checkmark briefly) -> back to idle. The recommendation
  // stays available to re-apply — it isn't a one-time thing that should vanish.
  const [applyState, setApplyState] = useState("idle");
  const [beforeCheckvalue, setBeforeCheckvalue] = useState(false);
  const [beforeDueDatedays, setbeforeDueDatedays] = useState(null);

  const [onDueDateCheckvalue, setOnDueDateCheckvalue] = useState(false);
  const [isAfterDueDateCheckvalue, setIsAfterDueDateCheckvalue] =
    useState(false);
  const [isOnSelectedDateValue, setIsOnSelectedDateValue] = useState(false);
  const [isParentOnlyEnable, setIsParentOnlyEnable] = useState(false);
  const [isOnSelectedDayOfWeekValue, setIsOnSelectedDayOfWeekValue] =
    useState(false);

  // const [isPerodicDailyCheckvalue, setIsPerodicDailyCheckvalue] =
  //   useState(false);
  // const [isPerodicWeeklyCheckvalue, setisPerodicWeeklyCheckvalue] =
  //   useState(false);
  // const [periodicWeeklydays, setperiodicWeeklydays] = useState(null);
  // const [periodicWeeklyhrs, setperiodicWeeklyhrs] = useState(null);
  // const [periodicWeeklymins, setperiodicWeeklymins] = useState(null);
  // const [selectFixedTime, seselectFixedTime] = useState(null);
  // const [perodicDailymins, setperodicDailymins] = useState(null);
  // const [perodicDailyhrs, setperodicDailyhrs] = useState(null);

  const [afterDueDatedy, setafterDueDatedy] = useState(null);
  const [
    isAfterInvoiceCreationCheckvalue,
    setIsAfterInvoiceCreationCheckvalue,
  ] = useState(false);
  const [afterInvoiceCreationDays, setAfterInvoiceCreationDays] =
    useState(null);
  const [onSelectedDueDate, setOnSelectedDueDate] = useState(null);
  const [onSelectedDueDayOfWeek, setOnSelectedDueDayOfWeek] =
    useState("monday");
  const [isEmailRemindersCheckvalue, setIsEmailRemindersCheckvalue] =
    useState(false);
  const [emailReminders, setEmailReminders] = useState(null);
  const [pastDueDays, setPastDueDays] = useState(null);
  const [isPastDueCheckvalue, setIsPastDueCheckvalue] = useState(false);
  const daysArray = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const [reminderRowData, setreminderRowData] = useState(null);
  const [children, setChildren] = useState([]);

  // True when every recommended field already matches the current (actual)
  // form values — lets the Apply button show "Already Applied" instead of
  // re-copying values that are already in place.
  const isRecommendationApplied = useMemo(() => {
    if (!recommendation?.recommendation) return false;
    const rec = recommendation.recommendation;
    const ms = rec.manageSettings || {};

    // Both sides coerced: the form values come straight off the saved record,
    // where an unset flag is undefined, and `false !== undefined` would read as
    // "not applied" for a recommendation that was applied and saved.
    const beforeOn = !!ms.beforeDueDate?.isBeforeDueDate;
    const afterOn = !!ms.afterDueDate?.isAfterDueDate;

    if (!!ms.onDueDate !== !!onDueDateCheckvalue) return false;
    if (beforeOn !== !!beforeCheckvalue) return false;
    if (afterOn !== !!isAfterDueDateCheckvalue) return false;
    // A day count only matters while its switch is on; with it off, the form
    // keeps whatever default it had and that shouldn't count as a difference.
    if (beforeOn && +(ms.beforeDueDate?.days ?? 1) !== +beforeDueDatedays) return false;
    if (afterOn && +(ms.afterDueDate?.days ?? 1) !== +afterDueDatedy) return false;

    if (ms.customerLevel) {
      const onDate = !!ms.customerLevel.isOnSelectedDate;
      const onWeekday = !!ms.customerLevel.isOnSelectedDayOfWeek;
      if (onDate !== !!isOnSelectedDateValue) return false;
      if (onWeekday !== !!isOnSelectedDayOfWeekValue) return false;
      if (onDate && ms.customerLevel.monthDate && +ms.customerLevel.monthDate !== +onSelectedDueDate) return false;
      if (
        onWeekday &&
        ms.customerLevel.weekDays &&
        ms.customerLevel.weekDays.toLowerCase() !== (onSelectedDueDayOfWeek || "").toLowerCase()
      )
        return false;
    }

    if (rec.daysToEscalate !== undefined) {
      if (!isPastDueCheckvalue || +rec.daysToEscalate !== +pastDueDays) return false;
    }
    if (rec.escalationEmailLimit !== undefined) {
      if (!isEmailRemindersCheckvalue || +rec.escalationEmailLimit !== +emailReminders) return false;
    }

    return true;
  }, [
    recommendation,
    onDueDateCheckvalue,
    beforeCheckvalue,
    beforeDueDatedays,
    isAfterDueDateCheckvalue,
    afterDueDatedy,
    isOnSelectedDateValue,
    isOnSelectedDayOfWeekValue,
    onSelectedDueDate,
    onSelectedDueDayOfWeek,
    isPastDueCheckvalue,
    pastDueDays,
    isEmailRemindersCheckvalue,
    emailReminders,
  ]);

  useEffect(() => {
    apiCall();
    loadRecommendation();
  }, []);

  const apiCall = async () => {
    try {
      let body = {
        customer_id: customer?.Id,
      };

      let res = await fetchData.mutateAsync({ body });
      console.log("res", res.data);
      if (res && res?.data?.data) {
        setreminderRowData(res.data.data);
        const settings = res.data.data.manageSettings || {};
        setBeforeCheckvalue(settings.beforeDueDate?.isBeforeDueDate ? true : false);
        setbeforeDueDatedays(settings.beforeDueDate?.days ?? 1);

        setOnDueDateCheckvalue(settings.onDueDate ? true : false);

        setIsAfterDueDateCheckvalue(settings.afterDueDate?.isAfterDueDate ? true : false);
        setafterDueDatedy(settings.afterDueDate?.days ?? 1);

        setIsAfterInvoiceCreationCheckvalue(settings.afterInvoiceCreation?.isAfterInvoiceCreation ? true : false);
        setAfterInvoiceCreationDays(settings.afterInvoiceCreation?.days ?? 1);

        setIsOnSelectedDateValue(settings.customerLevel?.isOnSelectedDate);
        setIsOnSelectedDayOfWeekValue(settings.customerLevel?.isOnSelectedDayOfWeek);
        setOnSelectedDueDate(settings.customerLevel?.monthDate ?? 1);
        setOnSelectedDueDayOfWeek(settings.customerLevel?.weekdays ? settings.customerLevel.weekdays.toLocaleLowerCase() : "monday");
        setIsParentOnlyEnable(settings.isParentOnlyEnable);

        setIsEmailRemindersCheckvalue(res.data.data.isEscalationEmailLimit);
        setEmailReminders(res.data.data.escalationEmailLimit);

        setIsPastDueCheckvalue(res.data.data.isDaysToEscalate ? true : false);
        setPastDueDays(res.data.data.daysToEscalate ?? 1);
        // setIsPerodicDailyCheckvalue(settings.afterDueDate?.perodicDaily);
        // setperodicDailyhrs(settings.afterDueDate?.selectFixedDayTimeHour);
        // setperodicDailymins(settings.afterDueDate?.selectFixedDayTimeMins);
        // setisPerodicWeeklyCheckvalue(settings.afterDueDate?.periodicWeekly);
        // setperiodicWeeklydays(settings.afterDueDate?.selectFixedTimeDay?.toLocaleLowerCase());
        // setperiodicWeeklyhrs(settings.afterDueDate?.selectFixedTimeHour);
        // setperiodicWeeklymins(settings.afterDueDate?.selectFixedTimeMins);
      }
      if (res?.data?.childrens) {
        setChildren(res.data.childrens ? res.data.childrens : []);
      }
    } catch (error) {
      console.log(error);
    }

  };

  // Runs alongside apiCall, not after it — chained behind the settings load
  // the panel sat on "No recommendation" for the whole first request.
  const loadRecommendation = async () => {
    setRecommendationStatus("loading");
    try {
      const recRes = await recommendationFetch.mutateAsync({
        counterPartyId: customer?.Id,
      });
      const recList = recRes?.data?.data;
      if (recList && recList.length > 0) {
        setRecommendation(recList[0]);
        setRecommendationStatus("ready");
      } else {
        setRecommendation(null);
        setRecommendationStatus("none");
      }
    } catch (error) {
      console.log(error);
      setRecommendation(null);
      setRecommendationStatus("none");
    }
  };

  useEffect(() => {
    if (childFunc) {
      childFunc.current = handleSubmit;
    }
  }, []);

  const handleSubmit = () => { };

  // Fills the form fields from the recommendation. Doesn't call the save API
  // itself — the form now holds the recommended values same as if the user
  // had set them by hand, and the existing Save button persists them.
  // Writes the recommendation and then reloads the form from what was actually
  // saved. Reloading rather than trusting the setters is deliberate: the
  // settings save is an update with no upsert, so if anything stopped the
  // write (no preferences row yet, for one) the left side would otherwise show
  // values that never reached the database.
  const persistRecommendation = async () => {
    const rec = recommendation.recommendation;
    const ms = rec.manageSettings || {};

    const beforeOn = !!ms.beforeDueDate?.isBeforeDueDate;
    const beforeDays = ms.beforeDueDate?.days ?? 1;
    const afterOn = !!ms.afterDueDate?.isAfterDueDate;
    const afterDays = ms.afterDueDate?.days ?? 1;
    const onDue = !!ms.onDueDate;
    const onDate = !!ms.customerLevel?.isOnSelectedDate;
    const onDayOfWeek = !!ms.customerLevel?.isOnSelectedDayOfWeek;
    const monthDate = ms.customerLevel?.monthDate ?? onSelectedDueDate ?? 1;
    // The recommendation writes weekDays; the settings record uses weekdays.
    const weekday = ms.customerLevel?.weekDays
      ? ms.customerLevel.weekDays.toLowerCase()
      : onSelectedDueDayOfWeek;
    const escalateOn = rec.daysToEscalate !== undefined;
    const escalateDays = escalateOn ? rec.daysToEscalate : pastDueDays;
    const emailLimitOn = rec.escalationEmailLimit !== undefined;
    const emailLimit = emailLimitOn ? rec.escalationEmailLimit : emailReminders;

    const body = {
      _id: reminderRowData?._id ? reminderRowData._id : undefined,
      customer_id: customer?.Id,
      counterPartyId: customer?.Id,
      manageSettings: {
        onDueDate: onDue,
        beforeDueDate: { isBeforeDueDate: beforeOn, days: +beforeDays },
        afterDueDate: { days: +afterDays, isAfterDueDate: afterOn },
        // Not something the AI recommends; kept as currently set so the
        // whole-subdocument save doesn't drop it.
        afterInvoiceCreation: {
          isAfterInvoiceCreation: isAfterInvoiceCreationCheckvalue,
          days: +(afterInvoiceCreationDays ?? 1),
        },
        customerLevel: {
          isOnSelectedDate: onDate,
          isOnSelectedDayOfWeek: onDayOfWeek,
          weekdays: weekday,
          monthDate: +monthDate,
        },
        isParentOnlyEnable: isParentOnlyEnable,
      },
      isDaysToEscalate: escalateOn,
      daysToEscalate: escalateDays,
      escalationEmailLimit: emailLimit,
      isEscalationEmailLimit: emailLimitOn,
    };

    setApplyState("saving");
    setApplyError("");
    try {
      await dataAdd.mutateAsync({ body });
      // Pull the saved record back so the manual side shows what is on disk.
      await apiCall();
      setApplyState("applied");
      setTimeout(() => setApplyState("idle"), 1100);
    } catch (err) {
      console.error("failed to save the applied recommendation", err);
      setApplyError(describeError(err, "Could not save."));
      setApplyState("idle");
    }
  };

  const applyRecommendation = async () => {
    if (!recommendation?.recommendation || applyState !== "idle" || isRecommendationApplied) return;
    // No template means reminders can't go out, and the settings save would
    // find no preferences row to update. Ask for one first.
    if (!isEmailTemplateSelected) {
      setShowTemplatePicker(true);
      return;
    }
    await persistRecommendation();
  };

  const describeError = (err, fallback) => {
    const status = err?.response?.status;
    const message =
      err?.response?.data?.ErrorModel?.errorMessage ||
      err?.response?.data?.message ||
      err?.message;
    return [fallback, status ? `(${status})` : null, message ? `— ${message}` : null]
      .filter(Boolean)
      .join(" ");
  };

  // Assigning the template is what creates the customer's preferences row, so
  // it has to land before the settings save can stick.
  const assignTemplateAndApply = async () => {
    if (!pickedTemplateId || applyState !== "idle") return;
    setApplyState("saving");
    setApplyError("");
    try {
      await EmailTemplateService.postAssignATemplateToCustomer({
        Id: customer?.Id,
        _id: pickedTemplateId,
      });
      setAssignedTemplateId(pickedTemplateId);
      setShowTemplatePicker(false);
    } catch (err) {
      console.error("failed to assign the email template", err);
      setApplyError(describeError(err, "Could not assign the template."));
      setApplyState("idle");
      return;
    }
    setApplyState("idle");
    await persistRecommendation();
  };

  return (
    <>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          {
            handleReminderDefaults && handleReminderDefaults(values);
          }

          const body = {
            _id: reminderRowData?._id ? reminderRowData._id : undefined,
            customer_id: customer?.Id,
            counterPartyId: customer?.Id,

            manageSettings: {
              onDueDate: onDueDateCheckvalue,
              beforeDueDate: {
                isBeforeDueDate: beforeCheckvalue,
                days: +beforeDueDatedays,
              },
              afterDueDate: {
                days: +afterDueDatedy,
                isAfterDueDate: isAfterDueDateCheckvalue,
                // perodicDaily: isPerodicDailyCheckvalue,
                // periodicWeekly: isPerodicWeeklyCheckvalue,
                // selectFixedTimeDay: periodicWeeklydays,
                // selectFixedTimeHour: +periodicWeeklyhrs,
                // selectFixedTimeMins: +periodicWeeklymins,
                // selectFixedDayTimeHour: +perodicDailyhrs,
                // selectFixedDayTimeMins: +perodicDailymins
              },
              afterInvoiceCreation: {
                isAfterInvoiceCreation: isAfterInvoiceCreationCheckvalue,
                days: +afterInvoiceCreationDays,
              },
              customerLevel: {
                isOnSelectedDate: isOnSelectedDateValue,
                isOnSelectedDayOfWeek: isOnSelectedDayOfWeekValue,
                weekdays: onSelectedDueDayOfWeek,
                monthDate: +onSelectedDueDate,
              },
              isParentOnlyEnable: isParentOnlyEnable,
            },
            isDaysToEscalate: isPastDueCheckvalue,
            daysToEscalate: pastDueDays,
            escalationEmailLimit: emailReminders,
            isEscalationEmailLimit: isEmailRemindersCheckvalue,
            // isbefore_due_date: beforeCheckvalue,
            // before_due_date_days: beforeDueDatedays,

            // is_on_due_date: onDueDateCheckvalue,

            // isAfterDueDate: isAfterDueDateCheckvalue,
            // after_due_date_days: afterDueDatedy,

            // isPerodicDaily: isPerodicDailyCheckvalue,
            // perodicDaily_hrs: perodicDailyhrs,
            // perodicDaily_mins: perodicDailymins,

            // isPerodicWeekly: isPerodicWeeklyCheckvalue,
            // perodicWeekly_days: periodicWeeklydays,
            // perodicWeekly_hrs: periodicWeeklyhrs,
            // perodicWeekly_mins: periodicWeeklymins,
          };
          // const res = await axios.post(`/customers/add/supplierssetting`, body);

          let res = await dataAdd.mutateAsync({ body });
          closeModal();
        }}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          dirty,
          isValid,
        }) => (
          <Form onSubmit={handleSubmit} className="mx-auto">
            {(customer && customer?.email) || childEmailCheck ? (
              <ReminderSection>
                <HeadingTextManageSettings style={{ paddingTop: "14px" }}>
                  Automatic Email Reminders
                </HeadingTextManageSettings>
                <PopoverLayout>
                <LeftColumn>
                <>
                  <Section style={{
                    borderBottom: "none"
                  }}>
                    <ColumnLabel>Set Manually</ColumnLabel>

                    {/*******************************After Invoice Creation*************************************** */}

                    <CheckboxWrapper>
                      <input
                        type="checkbox"
                        id="isAfterInvoiceCreation"
                        label="After invoice creation."
                        name="isAfterInvoiceCreation"
                        checked={isAfterInvoiceCreationCheckvalue}
                        disabled={
                          isEmailTemplateSelected !== "" &&
                            isEmailTemplateSelected !== undefined &&
                            isEmailTemplateSelected !== null
                            ? false
                            : true
                        }
                        onChange={(e) => {
                          if (e.target.checked === true) {
                            setIsAfterInvoiceCreationCheckvalue(true);
                          } else {
                            setIsAfterInvoiceCreationCheckvalue(false);
                          }
                        }}
                      />

                      <input
                        type="number"
                        min={1}
                        placeholder="date"
                        style={{ width: "40px" }}
                        id="afterInvoiceCreationDays"
                        name="afterInvoiceCreationDays"
                        value={afterInvoiceCreationDays}
                        disabled={
                          isEmailTemplateSelected !== "" &&
                            isEmailTemplateSelected !== undefined &&
                            isEmailTemplateSelected !== null
                            ? false
                            : true
                        }
                        onChange={(e) => {
                          setAfterInvoiceCreationDays(e.target.value);
                        }}
                      />

                      <label style={{ color: "#808186" }}>
                        days after the invoice date
                      </label>
                    </CheckboxWrapper>

                    {/*******************************Before the Due date*************************************** */}

                    <CheckboxWrapper>
                      <input
                        type="checkbox"
                        id="beforeduDate"
                        label="Before the Due date."
                        name="beforeduDate"
                        checked={beforeCheckvalue}
                        disabled={
                          isEmailTemplateSelected !== "" &&
                            isEmailTemplateSelected !== undefined &&
                            isEmailTemplateSelected !== null
                            ? false
                            : true
                        }
                        onChange={(e) => {
                          if (e.target.checked === true) {
                            setBeforeCheckvalue(true);
                          } else {
                            setBeforeCheckvalue(false);
                          }
                        }}
                      />
                      {/* <label style={{ color: "#808186" }} for="beforeduDate">
                        {" "}
                        Before the Due date.
                      </label> */}

                      <input
                        type="number"
                        min={1}
                        placeholder="date"
                        style={{ width: "40px" }}
                        id="beforeDueDatedays"
                        name="beforeDueDatedays"
                        value={beforeDueDatedays}
                        disabled={
                          isEmailTemplateSelected !== "" &&
                            isEmailTemplateSelected !== undefined &&
                            isEmailTemplateSelected !== null
                            ? false
                            : true
                        }
                        onChange={(e) => {
                          setbeforeDueDatedays(e.target.value);
                        }}
                      />

                      <label style={{ color: "#808186" }}>
                        days before the due date
                      </label>
                    </CheckboxWrapper>

                    {/*******************************On Due date section*************************************** */}

                    <CheckboxWrapper>
                      <input
                        type="checkbox"
                        id="onDueDate"
                        name="onDueDate"
                        checked={onDueDateCheckvalue}
                        disabled={
                          isEmailTemplateSelected !== "" &&
                            isEmailTemplateSelected !== undefined &&
                            isEmailTemplateSelected !== null
                            ? false
                            : true
                        }
                        onChange={(e) => {
                          if (e.target.checked === true) {
                            setOnDueDateCheckvalue(true);
                          } else {
                            setOnDueDateCheckvalue(false);
                          }
                        }}
                      />
                      <label style={{ color: "#808186" }}>On Due date.</label>
                    </CheckboxWrapper>

                    {/*******************************After Due Date*************************************** */}

                    <HeadingText>After Due Date</HeadingText>
                    <div
                      style={{
                        marginLeft: "16px",
                      }}
                    >
                      <CheckboxWrapper>
                        <input
                          type="checkbox"
                          id="isAfterDueDate"
                          name="isAfterDueDate"
                          checked={isAfterDueDateCheckvalue}
                          disabled={
                            isEmailTemplateSelected !== "" &&
                              isEmailTemplateSelected !== undefined &&
                              isEmailTemplateSelected !== null
                              ? false
                              : true
                          }
                          onChange={(e) => {
                            if (e.target.checked === true) {
                              setIsAfterDueDateCheckvalue(true);
                            } else {
                              setIsAfterDueDateCheckvalue(false);
                            }
                          }}
                        />
                        <label style={{ color: "#808186" }}>Every </label>

                        <input
                          type="number"
                          min={1}
                          placeholder="date"
                          style={{ width: "40px" }}
                          id="afterDueDatedy"
                          name="afterDueDatedy"
                          value={afterDueDatedy}
                          disabled={
                            isEmailTemplateSelected !== "" &&
                              isEmailTemplateSelected !== undefined &&
                              isEmailTemplateSelected !== null
                              ? false
                              : true
                          }
                          onChange={(e) => {
                            setafterDueDatedy(e.target.value);
                          }}
                        />
                        <label style={{ color: "#808186" }}>
                          {" "}
                          days till invoice is paid.
                        </label>
                      </CheckboxWrapper>
                      {/* <CheckboxWrapper>
                        <input
                          type="checkbox"
                          id="isPerodicDaily"
                          name="isPerodicDaily"
                          checked={isPerodicDailyCheckvalue}
                          disabled={(isEmailTemplateSelected !== '' && isEmailTemplateSelected !== undefined) ? false : true}
                          onChange={(e) => {
                            if (e.target.checked === true) {
                              setIsPerodicDailyCheckvalue(true);
                            } else {
                              setIsPerodicDailyCheckvalue(false);
                            }
                          }}
                        />
                        <label
                          style={{ color: "#808186" }}
                          for="isPerodicDaily"
                        >
                          {" "}
                          Periodic Daily.
                        </label>

                        <StyleTimePicker>
                          <input
                            type="number"
                            style={{ width: "35px" }}
                            id="perodicDailyhrs"
                            name="perodicDailyhrs"
                            value={perodicDailyhrs}
                            placeholder="Hrs"
                            disabled={(isEmailTemplateSelected !== '' && isEmailTemplateSelected !== undefined) ? false : true}
                            onChange={(e) => {
                              setperodicDailyhrs(e.target.value);
                            }}
                          />

                          <input
                            type="number"
                            style={{ width: "25px" }}
                            id="perodicDailymins"
                            name="perodicDailymins"
                            value={perodicDailymins}
                            placeholder="Mins"
                            disabled={(isEmailTemplateSelected !== '' && isEmailTemplateSelected !== undefined) ? false : true}
                            onChange={(e) => {
                              setperodicDailymins(e.target.value);
                            }}
                          />
                        </StyleTimePicker>
                      </CheckboxWrapper>

                      <CheckboxWrapper>
                        <input
                          type="checkbox"
                          id="isPerodicWeekly"
                          name="isPerodicWeekly"
                          checked={isPerodicWeeklyCheckvalue}
                          disabled={(isEmailTemplateSelected !== '' && isEmailTemplateSelected !== undefined) ? false : true}
                          onChange={(e) => {
                            if (e.target.checked === true) {
                              setisPerodicWeeklyCheckvalue(true);
                            } else {
                              setisPerodicWeeklyCheckvalue(false);
                            }
                          }}
                        />
                        <label
                          style={{ color: "#808186" }}
                          for="isPerodicWeekly"
                        >
                          {" "}
                          Periodic Weekly.
                        </label>
                        <select
                          id="periodicWeeklydays"
                          name="periodicWeeklydays"
                          value={periodicWeeklydays}
                          disabled={(isEmailTemplateSelected !== '' && isEmailTemplateSelected !== undefined) ? false : true}
                          onChange={(e) => {
                            setperiodicWeeklydays(e.target.value);
                          }}
                        >
                          {daysArray.map((elem) => (
                            <option value={elem.toLocaleLowerCase()}>
                              {elem}
                            </option>
                          ))}
                        </select>
                        <HeadingText>Days</HeadingText>

                        <StyleTimePicker>
                          <input
                            type="number"
                            style={{ width: "35px" }}
                            id="periodicWeeklyhrs"
                            name="periodicWeeklyhrs"
                            value={periodicWeeklyhrs}
                            placeholder="Hrs"
                            disabled={(isEmailTemplateSelected !== '' && isEmailTemplateSelected !== undefined) ? false : true}
                            onChange={(e) => {
                              setperiodicWeeklyhrs(e.target.value);
                            }}
                          />

                          <input
                            type="number"
                            style={{ width: "35px" }}
                            id="periodicWeeklymins"
                            name="periodicWeeklymins"
                            value={periodicWeeklymins}
                            placeholder="Mins"
                            disabled={(isEmailTemplateSelected !== '' && isEmailTemplateSelected !== undefined) ? false : true}
                            onChange={(e) => {
                              setperiodicWeeklymins(e.target.value);
                            }}
                          />
                        </StyleTimePicker>
                      </CheckboxWrapper> */}
                    </div>
                    <HeadingText>Customer Level</HeadingText>
                    <CheckboxWrapper>
                      <input
                        type="checkbox"
                        id="isOnSelectedDate"
                        name="isOnSelectedDate"
                        checked={isOnSelectedDateValue}
                        disabled={
                          isEmailTemplateSelected !== "" &&
                            isEmailTemplateSelected !== undefined &&
                            isEmailTemplateSelected !== null
                            ? false
                            : true
                        }
                        onChange={(e) => {
                          if (e.target.checked === true) {
                            setIsOnSelectedDateValue(true);
                          } else {
                            setIsOnSelectedDateValue(false);
                          }
                        }}
                      />

                      <label style={{ color: "#808186" }}>Day </label>
                      <input
                        type="number"
                        min={1}
                        style={{ width: "40px" }}
                        placeholder="date"
                        id="onSelectedDueDate"
                        name="onSelectedDueDate"
                        value={onSelectedDueDate}
                        disabled={
                          isEmailTemplateSelected !== "" &&
                            isEmailTemplateSelected !== undefined &&
                            isEmailTemplateSelected !== null
                            ? false
                            : true
                        }
                        onChange={(e) => {
                          setOnSelectedDueDate(e.target.value);
                        }}
                      />
                      <label style={{ color: "#808186" }}> of each month</label>
                    </CheckboxWrapper>

                    <CheckboxWrapper style={{ alignItems: "center" }}>
                      <input
                        type="checkbox"
                        id="isOnSelectedDayOfWeek"
                        name="isOnSelectedDayOfWeek"
                        checked={isOnSelectedDayOfWeekValue}
                        disabled={
                          isEmailTemplateSelected !== "" &&
                            isEmailTemplateSelected !== undefined &&
                            isEmailTemplateSelected !== null
                            ? false
                            : true
                        }
                        onChange={(e) => {
                          if (e.target.checked === true) {
                            setIsOnSelectedDayOfWeekValue(true);
                          } else {
                            setIsOnSelectedDayOfWeekValue(false);
                          }
                        }}
                      />

                      <select
                        id="onSelectedDueDayOfWeek"
                        name="onSelectedDueDayOfWeek"
                        style={weekdaySelectStyle}
                        value={onSelectedDueDayOfWeek}
                        disabled={
                          isEmailTemplateSelected !== "" &&
                            isEmailTemplateSelected !== undefined &&
                            isEmailTemplateSelected !== null
                            ? false
                            : true
                        }
                        onChange={(e) => {
                          setOnSelectedDueDayOfWeek(e.target.value);
                        }}
                      >
                        {daysArray.map((elem) => (
                          <option value={elem.toLocaleLowerCase()}>
                            {elem}
                          </option>
                        ))}
                      </select>
                      <label style={{ color: "#808186", whiteSpace: "nowrap", marginLeft: "8px" }}>
                        of every week
                      </label>
                    </CheckboxWrapper>
                    {parentRecord && (
                      <CheckboxWrapper>
                        <input
                          type="checkbox"
                          id="isParentOnlyEnable"
                          name="isParentOnlyEnable"
                          checked={isParentOnlyEnable}
                          disabled={
                            isEmailTemplateSelected !== "" &&
                              isEmailTemplateSelected !== undefined &&
                              isEmailTemplateSelected !== null
                              ? false
                              : true
                          }
                          onChange={(e) => {
                            if (e.target.checked === true) {
                              setIsParentOnlyEnable(true);
                            } else {
                              setIsParentOnlyEnable(false);
                            }
                          }}
                        />
                        <HeadingText>Enable email for parent only</HeadingText>
                      </CheckboxWrapper>
                    )}
                  </Section>
                  {/* <Section style={{
                    borderBottom: "none"
                  }}>
                    <HeadingText>
                      <InfoIcon style={{ marginRight: "10px" }} />
                      If None checked, No reminders are set
                    </HeadingText>
                    {(isEmailTemplateSelected === "" ||
                      isEmailTemplateSelected === undefined ||
                      isEmailTemplateSelected === null) && (
                      <HeadingText>
                        <InfoIcon style={{ marginRight: "10px" }} />
                        No Template is selected, select a template first
                      </HeadingText>
                    )}
                    {children.length > 1 ? (
                      <HeadingText>
                        <InfoIcon
                          style={{ marginRight: "10px" }}
                          data-tip={children.join(", ")}
                          data-for="info"
                        />
                        <Tooltip place="bottom" id="info" />
                        {customer.company} have {children.length} children
                      </HeadingText>
                    ) : null}
                  </Section> */}
                  <Section style={
                    {
                      borderTop: "solid 1px #707070",
                      borderBottom: "none",
                      paddingTop: "14px"
                    }
                  }>
                    <HeadingTextManageSettings>Escalation Rules</HeadingTextManageSettings>

                    {/*******************************Before the Due date*************************************** */}

                    <CheckboxWrapper>
                      <label style={{ color: "#808186" }}>
                        <input
                          type="checkbox"
                          id="pastDueCheckValue"
                          label="Before the Due date."
                          name="pastDueCheckValue"
                          checked={isPastDueCheckvalue}
                          disabled={
                            isEmailTemplateSelected !== "" &&
                              isEmailTemplateSelected !== undefined
                              ? false
                              : true
                          }
                          onChange={(e) => {
                            if (e.target.checked === true) {
                              setIsPastDueCheckvalue(true);
                            } else {
                              setIsPastDueCheckvalue(false);
                            }
                          }}
                        /> Escalate <input
                          type="number"
                          min={1}
                          placeholder="0"
                          style={{ width: "40px" }}
                          id="daysToEscalate"
                          name="daysToEscalate"
                          value={pastDueDays}
                          onChange={(e) => {
                            setPastDueDays(e.target.value);
                          }}
                        /> days past due.
                      </label>






                    </CheckboxWrapper>


                    <CheckboxWrapper>
                      <label style={{ color: "#808186" }}>
                        <input
                          type="checkbox"
                          id="emailRemindersCheckvalue"
                          label="Before the Due date."
                          name="emailRemindersCheckvalue"
                          checked={isEmailRemindersCheckvalue}
                          disabled={
                            isEmailTemplateSelected !== "" &&
                              isEmailTemplateSelected !== undefined
                              ? false
                              : true
                          }
                          onChange={(e) => {
                            if (e.target.checked === true) {
                              setIsEmailRemindersCheckvalue(true);
                            } else {
                              setIsEmailRemindersCheckvalue(false);
                            }
                          }}
                        /> Escalate after<input
                          type="number"
                          min={1}
                          placeholder="0"
                          style={{ width: "40px" }}
                          id="escalationEmailLimit"
                          name="escalationEmailLimit"
                          value={emailReminders}
                          disabled={
                            isEmailTemplateSelected !== "" &&
                              isEmailTemplateSelected !== undefined
                              ? false
                              : true
                          }
                          onChange={(e) => {
                            setEmailReminders(e.target.value);
                          }}
                        /> email reminders.
                      </label>

                      {/* <label style={{ color: "#808186" }} for="beforeduDate">
                        {" "}
                        Before the Due date.
                      </label> */}




                    </CheckboxWrapper>
                  </Section>
                  <BasicButton
                    style={{ margin: "auto 20px 20px auto" }}
                    type="submit"
                    width="110px"
                  >
                    Save
                  </BasicButton>
                </>
                </LeftColumn>
                <>
                  <VerticalDivider />
                  <RecommendationColumn>
                    {recommendationStatus === "loading" ? (
                      <LoadingHint>
                        <Spinner animation="border" variant="secondary" />
                        Generating your recommendation…
                      </LoadingHint>
                    ) : recommendationStatus === "none" || !recommendation?.recommendation ? (
                      <NoRecommendationHint>
                        No suggestion yet — this customer has no pending invoices to base one on.
                      </NoRecommendationHint>
                    ) : (
                      <RecommendationCard>
                        {/* Shown above the mirrored form rather than in place of it, so
                            the checkboxes don't vanish for a second on every apply. */}
                        {applyState === "applied" && (
                          <AppliedBanner>
                            <span className="check">✓</span>
                            Suggestion applied and saved
                          </AppliedBanner>
                        )}
                        <ColumnLabel>
                          Recommended by AI
                          <RiskBadge tier={recommendation.recommendation.riskTier}>
                            {recommendation.recommendation.riskTier}
                          </RiskBadge>
                        </ColumnLabel>

                        <PreviewForm>
                          {/* The manual column opens with "days after the invoice
                              date", which no recommendation covers. Reserve the same
                              row here, hidden, so everything beneath lines up. */}
                          <CheckboxWrapper style={{ visibility: "hidden" }} aria-hidden="true">
                            <input type="checkbox" readOnly checked={false} />
                            <input type="number" style={{ width: "40px" }} readOnly value="" />
                            <label style={{ color: "#808186" }}>days after the invoice date</label>
                          </CheckboxWrapper>

                          {/* Before the Due date */}
                          <CheckboxWrapper>
                            <input
                              type="checkbox"
                              readOnly
                              checked={Boolean(
                                recommendation.recommendation.manageSettings?.beforeDueDate?.isBeforeDueDate
                              )}
                            />
                            <input
                              type="number"
                              style={{ width: "40px" }}
                              readOnly
                              value={recommendation.recommendation.manageSettings?.beforeDueDate?.days ?? ""}
                            />
                            <label style={{ color: "#808186" }}> days before the due date</label>
                          </CheckboxWrapper>

                          {/* On Due date */}
                          <CheckboxWrapper>
                            <input
                              type="checkbox"
                              readOnly
                              checked={Boolean(recommendation.recommendation.manageSettings?.onDueDate)}
                            />
                            <label style={{ color: "#808186" }}>On Due date.</label>
                          </CheckboxWrapper>

                          {/* After Due Date */}
                          <HeadingText>After Due Date</HeadingText>
                          <div style={{ marginLeft: "16px" }}>
                            <CheckboxWrapper>
                              <input
                                type="checkbox"
                                readOnly
                                checked={Boolean(
                                  recommendation.recommendation.manageSettings?.afterDueDate?.isAfterDueDate
                                )}
                              />
                              <label style={{ color: "#808186" }}>Every </label>
                              <input
                                type="number"
                                style={{ width: "40px" }}
                                readOnly
                                value={recommendation.recommendation.manageSettings?.afterDueDate?.days ?? ""}
                              />
                              <label style={{ color: "#808186" }}> days till invoice is paid.</label>
                            </CheckboxWrapper>
                          </div>

                          {/* Customer Level */}
                          <HeadingText>Customer Level</HeadingText>
                          <CheckboxWrapper>
                            <input
                              type="checkbox"
                              readOnly
                              checked={Boolean(
                                recommendation.recommendation.manageSettings?.customerLevel?.isOnSelectedDate
                              )}
                            />
                            <label style={{ color: "#808186" }}>Day </label>
                            <input
                              type="number"
                              style={{ width: "40px" }}
                              readOnly
                              value={recommendation.recommendation.manageSettings?.customerLevel?.monthDate ?? ""}
                            />
                            <label style={{ color: "#808186" }}> of each month</label>
                          </CheckboxWrapper>
                          <CheckboxWrapper style={{ alignItems: "center" }}>
                            <input
                              type="checkbox"
                              readOnly
                              checked={Boolean(
                                recommendation.recommendation.manageSettings?.customerLevel?.isOnSelectedDayOfWeek
                              )}
                            />
                            <select
                              style={weekdaySelectStyle}
                              value={(
                                recommendation.recommendation.manageSettings?.customerLevel?.weekDays || ""
                              ).toLocaleLowerCase()}
                              onChange={() => {}}
                            >
                              {daysArray.map((elem) => (
                                <option key={elem} value={elem.toLocaleLowerCase()}>
                                  {elem}
                                </option>
                              ))}
                            </select>
                            <label style={{ color: "#808186", whiteSpace: "nowrap", marginLeft: "8px" }}>
                              of every week
                            </label>
                          </CheckboxWrapper>

                          {/* The left column carries a "parent only" row that a
                              recommendation has no equivalent for, which left the two
                              dividers off by that row's height. Reserving the same row
                              here — hidden, and only when the left actually renders it —
                              keeps the divider aligned without hardcoding a pixel offset
                              or inventing a setting the recommendation doesn't have. */}
                          {parentRecord && (
                            <CheckboxWrapper style={{ visibility: "hidden" }} aria-hidden="true">
                              <input type="checkbox" readOnly checked={false} />
                              <HeadingText>Enable email for parent only</HeadingText>
                            </CheckboxWrapper>
                          )}

                          {/* Escalation Rules — divider and heading weight match the
                              left column's own section break above its copy. */}
                          <div
                            style={{
                              // Mirrors the left column exactly: its preceding Section
                              // ends with 10px of padding before the border, and the
                              // escalation Section resumes 14px after it. The negative
                              // side margins pull the line out through the column's
                              // 16px padding so it spans edge to edge like the left.
                              borderTop: "1px solid #707070",
                              marginTop: "10px",
                              marginLeft: "-10px",
                              paddingTop: "14px",
                              paddingLeft: "10px",
                            }}
                          >
                            <HeadingTextManageSettings>Escalation Rules</HeadingTextManageSettings>
                          </div>
                          <CheckboxWrapper>
                            <input
                              type="checkbox"
                              readOnly
                              checked={recommendation.recommendation.daysToEscalate !== undefined}
                            />
                            <label style={{ color: "#808186" }}>Escalate </label>
                            <input
                              type="number"
                              style={{ width: "40px" }}
                              readOnly
                              value={recommendation.recommendation.daysToEscalate ?? ""}
                            />
                            <label style={{ color: "#808186" }}> days past due.</label>
                          </CheckboxWrapper>
                          <CheckboxWrapper>
                            <input
                              type="checkbox"
                              readOnly
                              checked={recommendation.recommendation.escalationEmailLimit !== undefined}
                            />
                            <label style={{ color: "#808186" }}>Escalate after </label>
                            <input
                              type="number"
                              style={{ width: "40px" }}
                              readOnly
                              value={recommendation.recommendation.escalationEmailLimit ?? ""}
                            />
                            <label style={{ color: "#808186" }}> email reminders.</label>
                          </CheckboxWrapper>
                        </PreviewForm>

                          {recommendation.recommendation.reasoning?.length > 0 && (
                            <ReasoningList>
                              {recommendation.recommendation.reasoning.map((line, i) => (
                                <li key={i}>{formatReasoning(line)}</li>
                              ))}
                            </ReasoningList>
                          )}
                          {showTemplatePicker ? (
                            <TemplatePicker>
                              <div className="hint">
                                This customer has no email template, so reminders can't be
                                sent yet. Choose one to use with this recommendation.
                              </div>
                              <select
                                value={pickedTemplateId}
                                onChange={(e) => setPickedTemplateId(e.target.value)}
                              >
                                <option value="">Select a template</option>
                                {templates.map((template) => (
                                  <option key={template._id} value={template._id}>
                                    {template.emailTemplateName || template.name || template._id}
                                  </option>
                                ))}
                              </select>
                              <BasicButton
                                type="button"
                                width="100%"
                                onClick={assignTemplateAndApply}
                                disabled={!pickedTemplateId || applyState === "saving"}
                              >
                                {applyState === "saving" ? "Saving…" : "Assign & Apply"}
                              </BasicButton>
                              <span
                                className="cancel"
                                onClick={() => {
                                  if (applyState !== "saving") setShowTemplatePicker(false);
                                }}
                              >
                                Cancel
                              </span>
                            </TemplatePicker>
                          ) : (
                            <BasicButton
                              type="button"
                              width="110px"
                              style={{
                                margin: "auto 4px 20px auto",
                                ...(isRecommendationApplied
                                  ? { opacity: 0.6, cursor: "not-allowed" }
                                  : {}),
                              }}
                              onClick={applyRecommendation}
                              disabled={isRecommendationApplied || applyState === "saving"}
                            >
                              {isRecommendationApplied
                                ? "Already Applied"
                                : applyState === "saving"
                                ? "Saving…"
                                : "Apply"}
                            </BasicButton>
                          )}
                          {applyError && <ApplyError>{applyError}</ApplyError>}
                        </RecommendationCard>
                      )}
                    </RecommendationColumn>
                  </>
                </PopoverLayout>
              </ReminderSection>
            ) : (
              <>
                <ReminderSection>
                  <div
                    style={{
                      color: "white",
                      textAlign: "center",
                      marginTop: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    Email id is missing.
                  </div>
                </ReminderSection>
              </>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
}
